import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Palette, Briefcase, ShieldCheck, Hammer } from "lucide-react";
import { GOALS, type GoalId } from "@/config/product";
import { SectionHeader, type Category } from "@/components/ds";
import { T } from "@/components/T";

const ICONS: Record<GoalId, typeof GraduationCap> = {
  learn: GraduationCap,
  create: Palette,
  work: Briefcase,
  secure: ShieldCheck,
  build: Hammer,
};

const ACCENT: Record<GoalId, Category> = {
  learn: "fluency",
  create: "creative",
  work: "video",
  secure: "cyber",
  build: "prompting",
};

/** "What do you want to do?" — outcome-led entry instead of a tool list. */
export const GoalPicker = () => (
  <section id="choose-goal" aria-labelledby="choose-goal-heading" className="py-14 sm:py-20 px-4">
    <div className="max-w-6xl mx-auto">
      <SectionHeader
        id="choose-goal-heading"
        title={<T>What do you want to do?</T>}
        description={<T>Pick a goal and we'll take you straight to the right place. No need to learn every tool first.</T>}
        align="center"
      />
      <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {GOALS.map((g) => {
          const Icon = ICONS[g.id];
          return (
            <li key={g.id}>
              <Link
                to={g.start.href}
                className="focus-ring group flex h-full flex-col rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary/60"
                style={{ ["--cat" as string]: `var(--category-${ACCENT[g.id]})` }}
              >
                <span className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl [background-color:hsl(var(--cat)/0.12)]">
                  <Icon className="h-5 w-5 [color:hsl(var(--cat))]" aria-hidden />
                </span>
                <span className="font-semibold text-foreground"><T>{g.label}</T></span>
                <span className="mt-1 flex-1 text-sm text-muted-foreground"><T>{g.outcome}</T></span>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                  <T>{g.start.label}</T> <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  </section>
);
